import { useState, useCallback } from 'react'

export type KeyedBooleanFn<T, U extends string | number = string | number> = (
  key: U
) => T

type UseKeyedBooleans<T extends string | number = string | number> = [
  KeyedBooleanFn<boolean, T>,
  { add: KeyedBooleanFn<void, T>; remove: KeyedBooleanFn<void, T> },
  T[]
]

/**
 * allows for multiple boolean states to be managed at once using keys
 *
 *
 * @example
 * const [loading, setLoading] = useKeyedBooleans()
 * const [visible, setVisible] = useKeyedBooleans()
 * ...
 * <Component
 *  key={(record) => record.id}
 *  loading={(record) => loading(record.id)}
 *  visible={visible(key)}
 *  onMutate={async (record) => {
 *    setLoading.add(record.id)
 *    await mutate({ ... })
 *    setLoading.remove(record.id)
 *  }}
 *  onShow={(key) => {
 *    setVisible.add(key)
 *  }}
 *  onHide={(key) => {
 *    setVisible.remove(key)
 *  }}
 * />
 * @see {@link UseKeyedBooleans}
 * @see {@link KeyedBooleanFn}
 */
export function useKeyedBooleans<
  T extends string | number = string | number
>(): UseKeyedBooleans<T> {
  const [keys, setKeys] = useState<T[]>([])
  const found = useCallback<KeyedBooleanFn<boolean, T>>(
    (key) =>
      typeof key === 'string' || typeof key === 'number'
        ? keys.includes(key)
        : keys.length > 0,
    [keys]
  )
  return [
    found,
    {
      add: (key) => setKeys((prev) => [...prev, key]),
      remove: (key) => setKeys((prev) => prev.filter((k) => k !== key)),
    },
    keys,
  ]
}
